import { useState } from "react";
import { AcDialog, Button } from "@cntrl/ui";
import { useBandContextOrNull, useCapability } from "../../app/bandContext.js";
import { useCalendarFeed, useRotateCalendarFeed } from "../../app/hooks/useConnections.js";
import { pushToast } from "../../app/toasts.js";
import styles from "./SettingsScreen.module.scss";

/**
 * Show Sync — the band's confirmed shows as a subscribable calendar feed (`.ics`), on Settings › Account beside
 * Google Calendar (PO; it used to be a card on Connections). Three actions (PO): Copy Link, Subscribe (Apple
 * Calendar / Outlook via `webcal:`), and Reset Link — in red, behind a warning — which replaces the link so every
 * existing subscription stops updating. A Download was dropped: a downloaded `.ics` is a dead one-off copy that
 * never updates and that no reset can recall.
 *
 * A calendar app cannot authenticate — it polls a bare URL — so the URL IS the credential, and the line says so.
 * The feed is the band's CONFIRMED shows only (`IPublicCalendarFeedQuery`) — never holds, never person_dates.
 * A band with no link yet gets one on its first action, so there is no separate "create" step.
 *
 * The feed is the BAND's, so the row draws only with a band mounted and for a seat that may manage it (the gate the
 * Connections card had). The Account tab is shared with the org shell, which has no band: there it draws nothing.
 */
export function CalendarSyncSection() {
  const band = useBandContextOrNull();
  const canManage = useCapability("band:manage");
  if (!band || !canManage) return null;
  return <CalendarSyncRow />;
}

function CalendarSyncRow() {
  const feed = useCalendarFeed();
  const rotate = useRotateCalendarFeed();
  const [confirmingReset, setConfirmingReset] = useState(false);

  /** The feed's absolute URL, minting the link first when the band has none. Built against the app's own origin: the
   *  server returns a relative path so no environment host is baked into a response. */
  async function feedUrl(): Promise<string | null> {
    const path = feed.data?.hasFeed ? feed.data.path : (await rotate.mutateAsync({ minting: true })).path;
    return path ? new URL(path, window.location.origin).toString() : null;
  }

  async function copy() {
    const url = await feedUrl();
    if (!url) return;
    await navigator.clipboard?.writeText(url);
    pushToast("Calendar link copied.", "info");
  }


  async function subscribe() {
    const url = await feedUrl();
    // webcal:// makes Apple Calendar (and Outlook) open a live SUBSCRIPTION rather than importing a one-off copy.
    if (url) window.location.assign(url.replace(/^https?:/, "webcal:"));
  }

  const busy = feed.isLoading || rotate.isPending;
  return (
    <div className={`${styles.toggleRow} ${styles.calendarRow}`}>
      <span className={styles.toggleText}>
        <span className={styles.toggleLabel}>Show Sync</span>
        <span className={styles.toggleDesc}>
          Anyone with this link can subscribe to your band&rsquo;s shows.
        </span>
      </span>
      <span className={styles.calendarActions}>
        {/* First in the row (PO). Only once there is a link to reset — the first Copy Link or Subscribe makes one. */}
        {feed.data?.hasFeed && (
          <Button variant="accent" size="sm" disabled={busy} onClick={() => setConfirmingReset(true)}>
            Reset Link
          </Button>
        )}
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void copy()}>
          Copy Link
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void subscribe()}>
          Subscribe
        </Button>
      </span>

      {confirmingReset && (
        <AcDialog
          title="Reset link?"
          message="Every calendar subscribed with the current link stops getting your shows. Anyone who should keep them will need the new link."
          confirmLabel="Reset Link"
          tone="danger"
          loading={rotate.isPending}
          onConfirm={() => rotate.mutate({ minting: false }, { onSettled: () => setConfirmingReset(false) })}
          onCancel={() => setConfirmingReset(false)}
        />
      )}
    </div>
  );
}
